import React, { useState, useEffect } from 'react';
import { IBanner, IAppScreen } from '../MobileAppTypes';
import { BannerController } from './BannerController';
import Button from '../../_Core/Button';
import { FormatDate } from '../../../utils/Tools';

interface IBannerFormProps {
  appId: string;
  banner?: IBanner;
  screens: IAppScreen[];
  onSaved: () => void;
  onCancel: () => void;
}

const emptyBanner = (): IBanner => ({
  Message: '',
  IsDismissible: false,
  AppliesToAllScreens: true,
  DisplayStartAtUtc: '',
  DisplayEndAtUtc: '',
  TargetScreenPublicIds: [],
});

const BannerForm: React.FC<IBannerFormProps> = ({ appId, banner, screens, onSaved, onCancel }) => {
  const [formData, setFormData] = useState<IBanner>(emptyBanner());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (banner) {
      setFormData({
        ...banner,
        // Stored as UTC; show in the datetime-local input using the browser's local time for editing.
        DisplayStartAtUtc: FormatDate(banner.DisplayStartAtUtc, 'input-date-time') ?? '',
        DisplayEndAtUtc: FormatDate(banner.DisplayEndAtUtc, 'input-date-time') ?? '',
        TargetScreenPublicIds: banner.TargetScreenPublicIds || [],
      });
    } else {
      setFormData(emptyBanner());
    }
  }, [banner]);

  const updateField = <K extends keyof IBanner>(field: K, value: IBanner[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as string]) {
      setErrors((prev) => ({ ...prev, [field as string]: '' }));
    }
  };

  const toggleTargetScreen = (publicId: string, checked: boolean) => {
    const current = formData.TargetScreenPublicIds || [];
    updateField('TargetScreenPublicIds', checked ? [...current, publicId] : current.filter((id) => id !== publicId));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.Message || formData.Message.trim() === '') {
      newErrors.Message = 'Banner message is required';
    }

    if (formData.DisplayStartAtUtc && formData.DisplayEndAtUtc && new Date(formData.DisplayEndAtUtc) <= new Date(formData.DisplayStartAtUtc)) {
      newErrors.DisplayEndAtUtc = 'Display end must be after display start';
    }

    if (!formData.AppliesToAllScreens && (!formData.TargetScreenPublicIds || formData.TargetScreenPublicIds.length === 0)) {
      newErrors.TargetScreenPublicIds = 'Pick at least one screen, or turn on "All screens"';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const bannerToSave: IBanner = {
        ...formData,
        DisplayStartAtUtc: formData.DisplayStartAtUtc || undefined,
        DisplayEndAtUtc: formData.DisplayEndAtUtc || undefined,
      };

      const response = await BannerController.saveBanner(appId, bannerToSave);
      if (response.Success) {
        onSaved();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="form-horizontal">
      <div className="form-group">
        <label className="control-label col-sm-3">Message *</label>
        <div className="col-sm-9">
          <textarea
            className="form-control"
            rows={3}
            maxLength={500}
            value={formData.Message}
            onChange={(e) => updateField('Message', e.target.value)}
            placeholder="What should the banner say?"
            disabled={isSubmitting}
          />
          {errors.Message && <span className="text-danger">{errors.Message}</span>}
        </div>
      </div>

      <div className="form-group">
        <label className="control-label col-sm-3">Dismissible</label>
        <div className="col-sm-9">
          <input type="checkbox" checked={formData.IsDismissible} onChange={(e) => updateField('IsDismissible', e.target.checked)} disabled={isSubmitting} />
          <span className="ml-2">Let the user dismiss this banner</span>
        </div>
      </div>

      <div className="form-group">
        <label className="control-label col-sm-3">All Screens</label>
        <div className="col-sm-9">
          <input type="checkbox" checked={formData.AppliesToAllScreens} onChange={(e) => updateField('AppliesToAllScreens', e.target.checked)} disabled={isSubmitting} />
          <span className="ml-2">Show this banner on every screen</span>
        </div>
      </div>

      {!formData.AppliesToAllScreens && (
        <div className="form-group">
          <label className="control-label col-sm-3">Pinned Screens</label>
          <div className="col-sm-9">
            {screens.length === 0 ? (
              <div className="text-muted">No screens configured yet.</div>
            ) : (
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '10px' }}>
                {screens.map((screen) => (
                  <div key={screen.PublicId} className="checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={!!screen.PublicId && (formData.TargetScreenPublicIds || []).includes(screen.PublicId)}
                        onChange={(e) => screen.PublicId && toggleTargetScreen(screen.PublicId, e.target.checked)}
                        disabled={isSubmitting}
                      />
                      {' '}{screen.MenuTitle || screen.Title}
                    </label>
                  </div>
                ))}
              </div>
            )}
            {errors.TargetScreenPublicIds && <span className="text-danger">{errors.TargetScreenPublicIds}</span>}
          </div>
        </div>
      )}

      <div className="form-group">
        <label className="control-label col-sm-3">Display Window</label>
        <div className="col-sm-9">
          <div className="flex" style={{ gap: '15px', flexWrap: 'wrap' }}>
            <div>
              <label className="control-label" style={{ display: 'block' }}>Start</label>
              <input
                type="datetime-local"
                className="form-control"
                value={formData.DisplayStartAtUtc || ''}
                onChange={(e) => updateField('DisplayStartAtUtc', e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="control-label" style={{ display: 'block' }}>End</label>
              <input
                type="datetime-local"
                className="form-control"
                value={formData.DisplayEndAtUtc || ''}
                onChange={(e) => updateField('DisplayEndAtUtc', e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>
          <small className="help-block text-muted">Leave both blank to keep this banner showing indefinitely (sticky). Times are in the fair's local time zone.</small>
          {errors.DisplayEndAtUtc && <span className="text-danger">{errors.DisplayEndAtUtc}</span>}
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-offset-3 col-sm-9 btn-group">
          <Button type="cancel" onClick={onCancel} disabled={isSubmitting} text="Cancel" />
          <Button type="save" onClick={handleSubmit} disabled={isSubmitting} text="Save" />
        </div>
      </div>
    </div>
  );
};

export default BannerForm;
